import type { FunctionComponent } from 'react';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { isDesktop } from '@trello/browser';
import { BusinessClassIcon } from '@trello/nachos/icons/business-class';

import { WorkspaceMembersList } from 'app/src/components/WorkspaceMembersList';
import { useWorkspaceGuestListQuery } from './WorkspaceGuestListQuery.generated';

import styles from './WorkspaceGuestList.less';

interface WorkspaceGuestListProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  memberItems: any[];
  memberTypeFilter: string;
  orgEnterpriseName?: string;
  orgIdEnterprise?: string;
  orgIsBC?: boolean;
  orgType: string;
  workspaceId: string;
}

export const WorkspaceGuestList: FunctionComponent<WorkspaceGuestListProps> = ({
  memberItems,
  memberTypeFilter,
  orgEnterpriseName,
  orgIdEnterprise,
  orgIsBC,
  orgType,
  workspaceId,
}) => {
  const { data: org } = useWorkspaceGuestListQuery({
    variables: { idOrganization: workspaceId },
    waitOn: ['None'],
  });

  const {
    multiBoardCollaboratorIds,
    singleBoardCollaboratorIds,
    deactivatedCollaboratorIds,
  } = useMemo(() => {
    const multiBoardIds: string[] = [];
    const singleBoardIds: string[] = [];
    const deactivatedIds: string[] = [];

    org?.organization?.collaborators.forEach((collaborator) => {
      if (collaborator?.boardCount === 1) {
        singleBoardIds.push(collaborator.id);
      } else if (collaborator?.boardCount && collaborator?.boardCount > 1) {
        multiBoardIds.push(collaborator.id);
      }
      if (collaborator?.activityBlocked) {
        deactivatedIds.push(collaborator.id);
      }
    });

    return {
      deactivatedCollaboratorIds: deactivatedIds,
      multiBoardCollaboratorIds: multiBoardIds,
      singleBoardCollaboratorIds: singleBoardIds,
    };
  }, [org?.organization?.collaborators]);

  // We want to show free language when we're on desktop, regardless of whether they are on BC
  const orgTypeCopy = isDesktop() ? 'free' : orgType;

  const multiBoardGuestCopy = useMemo(() => {
    if (orgTypeCopy === 'bc') {
      return (
        <FormattedMessage
          id="templates.workspace_guest_list.bc-multi-board-guests-are-members-of"
          description="Explanation about multi-board guests and Premium workspace plan."
          defaultMessage="Multi-board guests are members of more than one Workspace board and charged the same price as a Workspace member."
        />
      );
    }
    if (orgTypeCopy === 'standard') {
      return (
        <FormattedMessage
          id="templates.workspace_guest_list.standard-multi-board-guests-are-members-of"
          description="Explanation about multi-board guests and Standard workspace plan."
          defaultMessage="Multi-board guests are members of more than one Workspace board and charged the same price as a Workspace member."
        />
      );
    }
    return (
      <FormattedMessage
        id="templates.workspace_guest_list.free-multi-board-guests-are-members-of"
        description="Explanation about multi-board guests and free workspace plan."
        defaultMessage="Multi-board guests are members of more than one Workspace board."
      />
    );
  }, [orgTypeCopy]);

  const singleBoardGuestCopy = useMemo(() => {
    if (orgTypeCopy === 'bc') {
      return (
        <FormattedMessage
          id="templates.workspace_guest_list.bc-single-board-guests-are-members-of"
          description="Explanation of single board guests and how they apply to the Premium workspace plan."
          defaultMessage="Single-board guests are members of only one Workspace board and free on your Premium plan."
        />
      );
    }
    if (orgTypeCopy === 'standard') {
      return (
        <FormattedMessage
          id="templates.workspace_guest_list.standard-single-board-guests-are-members-of"
          description="Explanation of single board guests and how they apply to the Standard workspace plan."
          defaultMessage="Single-board guests are members of only one Workspace board and free on your Standard plan."
        />
      );
    }
    return (
      <FormattedMessage
        id="templates.workspace_guest_list.free-single-board-guests-are-members-of"
        description="Explanation of single board guests and how they apply to the workspace plan."
        defaultMessage="Single-board guests are members of only one Workspace board."
      />
    );
  }, [orgTypeCopy]);

  const singleBoardGuests = memberItems.filter(
    (guest) => guest.boardCount === 1,
  );
  const multiBoardGuests = memberItems.filter((guest) => guest.boardCount > 1);

  // Guests can be deactivated at the Atlassian account level. When they are
  // they don't count against the user limit and in the case of MBGs are not
  // billable. So we want to still show them in the list of guests, but we'd
  // like to exclude them from the overall count.
  const multiBoardGuestsCount = multiBoardCollaboratorIds.filter(
    (id) => !deactivatedCollaboratorIds.includes(id),
  ).length;
  const singleBoardGuestsCount = singleBoardCollaboratorIds.filter(
    (id) => !deactivatedCollaboratorIds.includes(id),
  ).length;

  const shouldShowMultiBoardGuests = multiBoardCollaboratorIds.length > 0;
  const shouldShowSingleBoardGuests = singleBoardCollaboratorIds.length > 0;

  return (
    <>
      <hr role="presentation" />
      {shouldShowMultiBoardGuests && (
        <div className={styles.orgGuestSection}>
          <h3>
            <FormattedMessage
              id="templates.workspace_guest_list.multi-board-guests"
              description="Header for the multi-board guests section including the count."
              defaultMessage="Multi-board guests ({count})"
              values={{
                count: multiBoardGuestsCount,
              }}
            />
            {orgIsBC && (
              <span className={styles.headingIcon}>
                <BusinessClassIcon size="small" />
              </span>
            )}
          </h3>
          <p>
            {multiBoardGuestCopy}
            {!isDesktop() && (
              <a
                target="_blank"
                href="https://help.trello.com/article/1123-multi-board-guests"
              >
                <FormattedMessage
                  id="templates.workspace_guest_list.learn-more"
                  description="Link to help article to learn more about multi-board guests."
                  defaultMessage="Learn more"
                />
              </a>
            )}
          </p>
          <WorkspaceMembersList
            collaboratorIds={multiBoardCollaboratorIds}
            memberItems={multiBoardGuests}
            memberTypeFilter={memberTypeFilter}
            orgIdEnterprise={orgIdEnterprise}
            orgEnterpriseName={orgEnterpriseName}
            workspaceId={workspaceId}
          />
        </div>
      )}
      {shouldShowSingleBoardGuests && (
        <div className={styles.orgGuestSection}>
          <h3>
            <FormattedMessage
              id="templates.workspace_guest_list.single-board-guests"
              description="Header for the single-board guests section including the count."
              defaultMessage="Single-board guests ({count})"
              values={{
                count: singleBoardGuestsCount,
              }}
            />
          </h3>
          <p>{singleBoardGuestCopy}</p>
          <WorkspaceMembersList
            collaboratorIds={singleBoardCollaboratorIds}
            memberItems={singleBoardGuests}
            memberTypeFilter={memberTypeFilter}
            orgIdEnterprise={orgIdEnterprise}
            orgEnterpriseName={orgEnterpriseName}
            workspaceId={workspaceId}
          />
        </div>
      )}
    </>
  );
};
